import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Calendar,
  Cpu,
  DollarSign,
  HardDrive,
  Loader2,
  RefreshCw,
  ShieldAlert,
  TrendingUp,
  Zap,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DatePicker } from "@/components/ui/date-picker";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/contexts/auth-context";
import { usePageTitle } from "@/hooks/use-page-title";
import { useBackfill, useDailySpend, useSpendSummary, useSpendTrend } from "@/hooks/use-spend";
import type { DailySpendEntry, SpendTrendPoint } from "@/lib/types";
import { cn } from "@/lib/utils";

// OKLCH palette — used for categorical buckets (GPU type, pricing model).
const CATEGORY_COLORS = [
  "oklch(0.55 0.15 250)", // blue
  "oklch(0.55 0.12 160)", // green
  "oklch(0.6 0.15 55)", // orange
  "oklch(0.55 0.15 300)", // violet
  "oklch(0.6 0.15 20)", // red
  "oklch(0.6 0.12 90)", // yellow
  "oklch(0.55 0.15 200)", // teal
  "oklch(0.55 0.12 340)", // pink
];

// Provider colors are stable across the whole page.
const PROVIDER_COLORS: Record<string, string> = {
  gcp: "oklch(0.55 0.15 250)",
  aws: "oklch(0.6 0.15 55)",
  nebius: "oklch(0.55 0.15 300)",
};

// Pricing model colors are stable across the whole page.
const PRICING_COLORS: Record<string, string> = {
  ondemand: "oklch(0.55 0.15 250)",
  spot: "oklch(0.55 0.12 160)",
  "n/a": "oklch(0.7 0.02 270)",
};

type RangePreset = "7d" | "30d" | "mtd" | "custom";
type SortDir = "asc" | "desc";
type ResourceSortKey = "resource" | "provider" | "gpu" | "pricing" | "cost";

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function formatDateString(dateValue: Date): string {
  return dateValue.toISOString().slice(0, 10);
}

function getLatestAvailable(): Date {
  const now = new Date();
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  today.setDate(today.getDate() - 2);
  return today;
}

function getDateRange(preset: RangePreset, customStart?: Date, customEnd?: Date) {
  const latestAvailable = getLatestAvailable();
  switch (preset) {
    case "7d": {
      const rangeStart = new Date(latestAvailable);
      rangeStart.setDate(rangeStart.getDate() - 6);
      return {
        start: formatDateString(rangeStart),
        end: formatDateString(latestAvailable),
        days: 7,
      };
    }
    case "30d": {
      const rangeStart = new Date(latestAvailable);
      rangeStart.setDate(rangeStart.getDate() - 29);
      return {
        start: formatDateString(rangeStart),
        end: formatDateString(latestAvailable),
        days: 30,
      };
    }
    case "mtd": {
      const rangeStart = new Date(
        Date.UTC(latestAvailable.getUTCFullYear(), latestAvailable.getUTCMonth(), 1),
      );
      const rangeDays = Math.max(
        Math.ceil((latestAvailable.getTime() - rangeStart.getTime()) / 86400000) + 1,
        1,
      );
      return {
        start: formatDateString(rangeStart),
        end: formatDateString(latestAvailable),
        days: rangeDays,
      };
    }
    case "custom": {
      if (customStart && customEnd) {
        const rangeDays = Math.max(
          Math.ceil((customEnd.getTime() - customStart.getTime()) / 86400000) + 1,
          1,
        );
        return {
          start: formatDateString(customStart),
          end: formatDateString(customEnd),
          days: rangeDays,
        };
      }
      return {
        start: formatDateString(latestAvailable),
        end: formatDateString(latestAvailable),
        days: 1,
      };
    }
  }
}

function getMtdRange() {
  const latestAvailable = getLatestAvailable();
  const monthStart = new Date(
    Date.UTC(latestAvailable.getUTCFullYear(), latestAvailable.getUTCMonth(), 1),
  );
  return {
    start: formatDateString(monthStart),
    end: formatDateString(latestAvailable),
    label: latestAvailable.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    }),
  };
}

function formatUsd(amount: number): string {
  return `$${amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function getProviderBadge(provider: string) {
  const badgeColors: Record<string, string> = {
    gcp: "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300",
    aws: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
    nebius: "bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300",
  };
  return (
    <Badge
      variant="outline"
      className={cn("text-[10px] font-bold uppercase", badgeColors[provider])}
    >
      {provider}
    </Badge>
  );
}

function getPricingBadge(pricing: string | null) {
  if (!pricing) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  const styles: Record<string, string> = {
    spot: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
    ondemand: "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300",
  };
  return (
    <Badge variant="outline" className={cn("text-[10px] font-semibold uppercase", styles[pricing])}>
      {pricing}
    </Badge>
  );
}

/** Build a stable color map for a known set of keys. */
function buildColorMap(keys: string[], fixed: Record<string, string>): Record<string, string> {
  const map: Record<string, string> = { ...fixed };
  let fallbackIndex = 0;
  for (const key of keys) {
    if (!(key in map)) {
      // biome-ignore lint: palette is statically defined
      map[key] = CATEGORY_COLORS[fallbackIndex % CATEGORY_COLORS.length]!;
      fallbackIndex += 1;
    }
  }
  return map;
}

/** Derive sorted provider names from trend data (by total descending). */
function deriveProviders(data: SpendTrendPoint[]): string[] {
  const totals: Record<string, number> = {};
  for (const point of data) {
    for (const [provider, cost] of Object.entries(point.by_provider)) {
      totals[provider] = (totals[provider] ?? 0) + cost;
    }
  }
  return Object.entries(totals)
    .sort(([, costA], [, costB]) => costB - costA)
    .map(([provider]) => provider);
}

// Shared chart styling
const CHART_GRID = { strokeDasharray: "3 3", stroke: "var(--color-border)", opacity: 0.5 };
const CHART_AXIS = {
  tick: { fontSize: 11, fontFamily: "var(--font-mono)", fill: "var(--color-muted-foreground)" },
  tickLine: false as const,
  axisLine: false as const,
};
const TOOLTIP_STYLE = {
  contentStyle: {
    backgroundColor: "var(--popover)",
    color: "var(--popover-foreground)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    fontSize: 12,
    fontFamily: "var(--font-mono)",
  },
  wrapperStyle: { zIndex: 50 },
};

// ---------------------------------------------------------------------------
// Latest-day per-resource table
// ---------------------------------------------------------------------------

function LatestDayTable({ entries }: { entries: DailySpendEntry[] }) {
  const [sortKey, setSortKey] = useState<ResourceSortKey>("cost");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const sorted = useMemo(() => {
    const rows = [...entries];
    rows.sort((rowA, rowB) => {
      const selector = (entry: DailySpendEntry) => {
        switch (sortKey) {
          case "resource":
            return entry.resource_name;
          case "provider":
            return entry.provider;
          case "gpu":
            return entry.gpu_type ?? "";
          case "pricing":
            return entry.pricing_model ?? "";
          case "cost":
            return entry.cost;
        }
      };
      const valueA = selector(rowA);
      const valueB = selector(rowB);
      if (typeof valueA === "string" && typeof valueB === "string") {
        return sortDir === "asc" ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      }
      return sortDir === "asc"
        ? (valueA as number) - (valueB as number)
        : (valueB as number) - (valueA as number);
    });
    return rows;
  }, [entries, sortKey, sortDir]);

  function toggleSort(key: ResourceSortKey) {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir(key === "cost" ? "desc" : "asc");
    }
  }

  function SortIcon({ column }: { column: ResourceSortKey }) {
    if (sortKey !== column) return <ArrowUpDown className="ml-1 inline size-3 opacity-30" />;
    return sortDir === "asc" ? (
      <ArrowUp className="ml-1 inline size-3 text-primary" />
    ) : (
      <ArrowDown className="ml-1 inline size-3 text-primary" />
    );
  }

  const total = entries.reduce((sum, entry) => sum + entry.cost, 0);

  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between">
        <span className="text-[11px] font-medium text-muted-foreground">
          {entries[0]?.spend_date ?? ""} · {entries.length} resources
        </span>
        <span className="font-mono text-sm font-bold">{formatUsd(total)}</span>
      </div>
      <div className="max-h-[240px] overflow-y-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead
                className="cursor-pointer select-none text-xs"
                onClick={() => toggleSort("resource")}
              >
                Resource
                <SortIcon column="resource" />
              </TableHead>
              <TableHead
                className="cursor-pointer select-none text-xs"
                onClick={() => toggleSort("provider")}
              >
                Provider
                <SortIcon column="provider" />
              </TableHead>
              <TableHead
                className="cursor-pointer select-none text-xs"
                onClick={() => toggleSort("gpu")}
              >
                GPU
                <SortIcon column="gpu" />
              </TableHead>
              <TableHead
                className="cursor-pointer select-none text-xs"
                onClick={() => toggleSort("pricing")}
              >
                Pricing
                <SortIcon column="pricing" />
              </TableHead>
              <TableHead
                className="cursor-pointer select-none text-right text-xs"
                onClick={() => toggleSort("cost")}
              >
                Cost
                <SortIcon column="cost" />
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((entry) => (
              <TableRow key={`${entry.provider}:${entry.resource_name}`} className="h-8">
                <TableCell className="py-1.5 font-mono text-xs">{entry.resource_name}</TableCell>
                <TableCell className="py-1.5">{getProviderBadge(entry.provider)}</TableCell>
                <TableCell className="py-1.5 text-xs">{entry.gpu_type ?? "—"}</TableCell>
                <TableCell className="py-1.5">{getPricingBadge(entry.pricing_model)}</TableCell>
                <TableCell className="py-1.5 text-right font-mono text-sm font-medium">
                  {formatUsd(entry.cost)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Month Accumulation Area Chart (stacked by provider, cumulative)
// ---------------------------------------------------------------------------

function MonthAccumulationChart({
  data,
  providers,
  colorMap,
}: {
  data: SpendTrendPoint[];
  providers: string[];
  colorMap: Record<string, string>;
}) {
  const chartData = useMemo(() => {
    const cumulative: Record<string, number> = {};
    return data.map((point) => {
      for (const provider of providers) {
        cumulative[provider] = (cumulative[provider] ?? 0) + (point.by_provider[provider] ?? 0);
      }
      return {
        date: point.date.slice(5),
        ...Object.fromEntries(
          providers.map((provider) => [
            provider,
            Math.round((cumulative[provider] ?? 0) * 100) / 100,
          ]),
        ),
      };
    });
  }, [data, providers]);

  if (data.length === 0) {
    return (
      <div className="flex h-[280px] items-center justify-center text-sm text-muted-foreground">
        No data for this month
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={280}>
      <AreaChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
        <defs>
          {providers.map((provider) => {
            const color = colorMap[provider] ?? "var(--color-muted)";
            return (
              <linearGradient key={provider} id={`grad-${provider}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.4} />
                <stop offset="100%" stopColor={color} stopOpacity={0.05} />
              </linearGradient>
            );
          })}
        </defs>
        <CartesianGrid {...CHART_GRID} />
        <XAxis dataKey="date" {...CHART_AXIS} minTickGap={30} />
        <YAxis
          {...CHART_AXIS}
          tickFormatter={(value: number) =>
            `$${value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value}`
          }
        />
        <Tooltip {...TOOLTIP_STYLE} formatter={(value) => formatUsd(Number(value))} />
        {providers.map((provider) => (
          <Area
            key={provider}
            type="monotone"
            dataKey={provider}
            stackId="spend"
            stroke={colorMap[provider]}
            strokeWidth={1.5}
            fill={`url(#grad-${provider})`}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
}

// ---------------------------------------------------------------------------
// Daily Stacked Bar Chart (stacked by provider)
// ---------------------------------------------------------------------------

function DailyBarChart({
  data,
  providers,
  colorMap,
}: {
  data: SpendTrendPoint[];
  providers: string[];
  colorMap: Record<string, string>;
}) {
  const chartData = useMemo(
    () => data.map((point) => ({ date: point.date.slice(5), ...point.by_provider })),
    [data],
  );

  if (data.length === 0) {
    return (
      <div className="flex h-[280px] items-center justify-center text-sm text-muted-foreground">
        No data for this period
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
        <CartesianGrid {...CHART_GRID} />
        <XAxis dataKey="date" {...CHART_AXIS} minTickGap={20} />
        <YAxis {...CHART_AXIS} tickFormatter={(value: number) => `$${value}`} />
        <Tooltip {...TOOLTIP_STYLE} formatter={(value) => formatUsd(Number(value))} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {providers.map((provider, index) => (
          <Bar
            key={provider}
            dataKey={provider}
            stackId="spend"
            fill={colorMap[provider]}
            radius={index === providers.length - 1 ? [2, 2, 0, 0] : undefined}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

// ---------------------------------------------------------------------------
// Donut Chart
// ---------------------------------------------------------------------------

function SpendDonut({
  data,
  colorMap,
}: {
  data: { name: string; value: number }[];
  colorMap: Record<string, string>;
}) {
  if (data.length === 0) {
    return (
      <div className="flex h-[200px] items-center justify-center text-sm text-muted-foreground">
        No data
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={200}>
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          innerRadius={50}
          outerRadius={80}
          paddingAngle={2}
          stroke="none"
        >
          {data.map((entry) => (
            <Cell key={entry.name} fill={colorMap[entry.name] ?? "var(--color-muted)"} />
          ))}
        </Pie>
        <Tooltip {...TOOLTIP_STYLE} formatter={(value) => formatUsd(Number(value))} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

// ---------------------------------------------------------------------------
// Summary Card
// ---------------------------------------------------------------------------

function SummaryCard({
  label,
  value,
  icon: Icon,
  subtitle,
}: {
  label: string;
  value: string;
  icon: React.ComponentType<{ className?: string }>;
  subtitle?: string;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-4 p-4">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
          <Icon className="size-5 text-primary" />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            {label}
          </p>
          <p className="font-mono text-xl font-bold tracking-tight text-foreground">{value}</p>
          {subtitle && <p className="text-[11px] text-muted-foreground">{subtitle}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export function SpendPage() {
  usePageTitle("Cloud Spend");
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  const dateParam = searchParams.get("date");

  const [range, setRange] = useState<RangePreset>(dateParam ? "custom" : "30d");
  const [customStart, setCustomStart] = useState<Date | undefined>(
    dateParam ? new Date(dateParam) : undefined,
  );
  const [customEnd, setCustomEnd] = useState<Date | undefined>(
    dateParam ? new Date(dateParam) : undefined,
  );

  const { start, end, days } = getDateRange(range, customStart, customEnd);
  const mtd = getMtdRange();

  const { data: summary, isLoading: summaryLoading } = useSpendSummary(start, end);
  const { data: trend = [], isLoading: trendLoading } = useSpendTrend({ start, end });
  const { data: mtdTrend = [], isLoading: mtdLoading } = useSpendTrend({
    start: mtd.start,
    end: mtd.end,
  });
  const { data: mtdSummary } = useSpendSummary(mtd.start, mtd.end);

  // Latest-day per-resource breakdown — fed from /daily for the last day of MTD.
  const { data: latestDayEntries = [], isLoading: latestDayLoading } = useDailySpend(mtd.end);

  const [backfillStart, setBackfillStart] = useState<Date | undefined>();
  const [backfillEnd, setBackfillEnd] = useState<Date | undefined>();
  const backfill = useBackfill();

  const mtdProviders = useMemo(() => deriveProviders(mtdTrend), [mtdTrend]);
  const rangeProviders = useMemo(() => deriveProviders(trend), [trend]);

  const providerColorMap = useMemo(
    () => buildColorMap([...mtdProviders, ...rangeProviders], PROVIDER_COLORS),
    [mtdProviders, rangeProviders],
  );

  const gpuTypeColorMap = useMemo(() => {
    const keys = summary?.by_gpu_type.map((entry) => entry.gpu_type) ?? [];
    return buildColorMap(keys, {});
  }, [summary]);

  const pricingColorMap = useMemo(() => {
    const keys = summary?.by_pricing_model.map((entry) => entry.pricing_model) ?? [];
    return buildColorMap(keys, PRICING_COLORS);
  }, [summary]);

  const categoryColorMap = useMemo(() => {
    const keys = summary?.by_category.map((entry) => entry.category) ?? [];
    return buildColorMap(keys, {});
  }, [summary]);

  if (!user?.is_admin) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <ShieldAlert className="mx-auto mb-3 size-10 text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">Admin access required</p>
        </div>
      </div>
    );
  }

  const latestDate = getLatestAvailable();

  return (
    <div className="px-4 py-4 sm:px-6 sm:py-5 space-y-5">
      {/* Top section: Latest-day resource table (left) + Month accumulation chart (right) */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Latest Day</CardTitle>
          </CardHeader>
          <CardContent>
            {latestDayLoading ? (
              <Skeleton className="h-[280px]" />
            ) : latestDayEntries.length > 0 ? (
              <LatestDayTable entries={latestDayEntries} />
            ) : (
              <div className="flex h-[280px] items-center justify-center text-sm text-muted-foreground">
                No data
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2 overflow-visible">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium">{mtd.label} — Month to Date</CardTitle>
              {mtdSummary && (
                <span className="font-mono text-lg font-bold text-primary">
                  {formatUsd(mtdSummary.total_cost)}
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {mtdLoading ? (
              <Skeleton className="h-[280px]" />
            ) : (
              <MonthAccumulationChart
                data={mtdTrend}
                providers={mtdProviders}
                colorMap={providerColorMap}
              />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Range selector */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5 rounded-lg border bg-background p-1">
          {(["7d", "30d", "mtd"] as const).map((preset) => (
            <Button
              key={preset}
              size="sm"
              variant={range === preset ? "default" : "ghost"}
              className="h-7 px-3 text-xs"
              onClick={() => setRange(preset)}
            >
              {preset === "mtd" ? "MTD" : preset === "7d" ? "7 Days" : "30 Days"}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <DatePicker
            value={customStart}
            onChange={(selectedDate) => {
              setCustomStart(selectedDate);
              setRange("custom");
            }}
          />
          <span className="text-xs text-muted-foreground">to</span>
          <DatePicker
            value={customEnd}
            onChange={(selectedDate) => {
              setCustomEnd(selectedDate);
              setRange("custom");
            }}
          />
        </div>
        <div className="ml-auto text-[11px] text-muted-foreground">
          <Calendar className="mr-1 inline size-3" />
          Data through{" "}
          {latestDate.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            timeZone: "UTC",
          })}{" "}
          (~48h billing lag)
        </div>
      </div>

      {/* Summary Cards */}
      {summaryLoading ? (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <Card key={index}>
              <CardContent className="p-4">
                <Skeleton className="h-14" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : summary ? (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <SummaryCard
            label="Total Spend"
            value={formatUsd(summary.total_cost)}
            icon={DollarSign}
            subtitle={`${start} — ${end}`}
          />
          <SummaryCard
            label="Daily Average"
            value={formatUsd(summary.daily_average)}
            icon={TrendingUp}
          />
          <SummaryCard label="Resources" value={String(summary.machine_count)} icon={HardDrive} />
          <SummaryCard
            label="Top GPU Type"
            value={summary.top_gpu_type ?? "—"}
            icon={Cpu}
            subtitle={summary.top_gpu_type ? formatUsd(summary.top_gpu_type_cost) : undefined}
          />
        </div>
      ) : null}

      {/* Charts row: stacked bars (by provider) + 3 donuts */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2 overflow-visible">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Daily Cost by Provider</CardTitle>
          </CardHeader>
          <CardContent>
            {trendLoading ? (
              <Skeleton className="h-[280px]" />
            ) : (
              <DailyBarChart data={trend} providers={rangeProviders} colorMap={providerColorMap} />
            )}
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">By Provider</CardTitle>
            </CardHeader>
            <CardContent>
              {summaryLoading ? (
                <Skeleton className="h-[200px]" />
              ) : (
                <SpendDonut
                  data={
                    summary?.by_provider.map((provider) => ({
                      name: provider.provider,
                      value: provider.total_cost,
                    })) ?? []
                  }
                  colorMap={providerColorMap}
                />
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* What the money was actually spent on: GPU (model × pricing), CPU,
          shared FS, network SSD, other. Sums to total_cost. */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">By Category</CardTitle>
        </CardHeader>
        <CardContent>
          {summaryLoading ? (
            <Skeleton className="h-[200px]" />
          ) : (
            <SpendDonut
              data={
                summary?.by_category.map((entry) => ({
                  name: entry.category,
                  value: entry.total_cost,
                })) ?? []
              }
              colorMap={categoryColorMap}
            />
          )}
        </CardContent>
      </Card>

      {/* Second charts row: By GPU Type + By Pricing Model donuts */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">By GPU Type</CardTitle>
          </CardHeader>
          <CardContent>
            {summaryLoading ? (
              <Skeleton className="h-[200px]" />
            ) : (
              <SpendDonut
                data={
                  summary?.by_gpu_type.map((entry) => ({
                    name: entry.gpu_type,
                    value: entry.total_cost,
                  })) ?? []
                }
                colorMap={gpuTypeColorMap}
              />
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">By Pricing Model</CardTitle>
          </CardHeader>
          <CardContent>
            {summaryLoading ? (
              <Skeleton className="h-[200px]" />
            ) : (
              <SpendDonut
                data={
                  summary?.by_pricing_model.map((entry) => ({
                    name: entry.pricing_model,
                    value: entry.total_cost,
                  })) ?? []
                }
                colorMap={pricingColorMap}
              />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Spend by GPU type table */}
      {summary && summary.by_gpu_type.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Cpu className="size-4" />
              Spend by GPU Type
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]">#</TableHead>
                  <TableHead>GPU Type</TableHead>
                  <TableHead className="text-right">Resources</TableHead>
                  <TableHead className="text-right">Total Cost</TableHead>
                  <TableHead className="text-right">Daily Avg</TableHead>
                  <TableHead className="w-[140px]">Share</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {summary.by_gpu_type.map((entry, index) => {
                  const share =
                    summary.total_cost > 0 ? (entry.total_cost / summary.total_cost) * 100 : 0;
                  const dailyAvg = days > 0 ? entry.total_cost / days : 0;
                  const color = gpuTypeColorMap[entry.gpu_type] ?? "var(--color-muted)";
                  return (
                    <TableRow key={entry.gpu_type}>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {index + 1}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div
                            className="size-2.5 shrink-0 rounded-full"
                            style={{ backgroundColor: color }}
                          />
                          <span className="font-medium font-mono">{entry.gpu_type}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {entry.machine_count}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm font-semibold">
                        {formatUsd(entry.total_cost)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm text-muted-foreground">
                        {formatUsd(dailyAvg)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="h-2 flex-1 rounded-full bg-muted">
                            <div
                              className="h-2 rounded-full"
                              style={{
                                width: `${Math.min(share, 100)}%`,
                                backgroundColor: color,
                              }}
                            />
                          </div>
                          <span className="w-10 text-right font-mono text-[10px] text-muted-foreground">
                            {share.toFixed(0)}%
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Spend by pricing model table */}
      {summary && summary.by_pricing_model.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Zap className="size-4" />
              Spend by Pricing Model
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {summary.by_pricing_model.map((entry) => {
                const share =
                  summary.total_cost > 0 ? (entry.total_cost / summary.total_cost) * 100 : 0;
                return (
                  <div
                    key={entry.pricing_model}
                    className="flex items-center gap-3 rounded-lg border px-4 py-3"
                  >
                    {getPricingBadge(entry.pricing_model)}
                    <div>
                      <p className="font-mono text-lg font-bold leading-none">
                        {formatUsd(entry.total_cost)}
                      </p>
                      <p className="mt-1 text-[10px] text-muted-foreground">
                        {share.toFixed(0)}% of total
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Provider breakdown — existing layout */}
      {summary && summary.by_provider.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Spend by Provider</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {summary.by_provider.map((provider) => (
                <div
                  key={provider.provider}
                  className="flex items-center gap-3 rounded-lg border px-4 py-3"
                >
                  {getProviderBadge(provider.provider)}
                  <span className="font-mono text-lg font-bold">
                    {formatUsd(provider.total_cost)}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Backfill section */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <RefreshCw className="size-4" />
            Backfill Historical Data
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <p className="mb-1 text-[11px] font-medium text-muted-foreground">Start Date</p>
              <DatePicker value={backfillStart} onChange={setBackfillStart} />
            </div>
            <div>
              <p className="mb-1 text-[11px] font-medium text-muted-foreground">End Date</p>
              <DatePicker value={backfillEnd} onChange={setBackfillEnd} />
            </div>
            <Button
              size="sm"
              disabled={!backfillStart || !backfillEnd || backfill.isPending}
              onClick={() => {
                if (backfillStart && backfillEnd) {
                  backfill.mutate({
                    start: formatDateString(backfillStart),
                    end: formatDateString(backfillEnd),
                  });
                }
              }}
            >
              {backfill.isPending && <Loader2 className="mr-1.5 size-3 animate-spin" />}
              Run Backfill
            </Button>
            {backfill.isSuccess && (
              <span className="text-xs text-green-600 dark:text-green-400">
                {backfill.data.message}
              </span>
            )}
            {backfill.isError && (
              <span className="text-xs text-destructive">
                {backfill.error instanceof Error ? backfill.error.message : "Failed"}
              </span>
            )}
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">
            Fetches AWS Cost Explorer + GCP BigQuery + Nebius FOCUS export data for the selected
            range. Runs in the background.
          </p>
        </CardContent>
      </Card>

      {/* Empty state */}
      {!summaryLoading && summary && summary.total_cost === 0 && trend.length === 0 && (
        <div className="rounded-lg border border-dashed p-8 text-center">
          <DollarSign className="mx-auto mb-3 size-8 text-muted-foreground/40" />
          <p className="text-sm font-medium text-muted-foreground">No spend data yet</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Use the backfill tool above to import historical billing data, or wait for the daily
            snapshot at 09:00 Israel time.
          </p>
        </div>
      )}
    </div>
  );
}
